import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import memoryAdapter from 'pouchdb-adapter-memory';
import { addRxPlugin, createRxDatabase } from 'rxdb';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { RxStorageLokiStatics } from 'rxdb/plugins/lokijs';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration';
import { addPouchPlugin, getRxStoragePouch } from 'rxdb/plugins/pouchdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { RxDBReplicationGraphQLPlugin } from 'rxdb/plugins/replication-graphql';
import { RxDBUpdatePlugin } from 'rxdb/plugins/update';
import { RxDBValidatePlugin } from 'rxdb/plugins/validate';
import { getRxStorageWorker } from 'rxdb/plugins/worker';

import { AimsDatabaseCollections } from '../models/models';
import { isJestTest } from '../test-helpers/isJestTest';
import { assetDetailsSchema } from './assetDetails/assetDetails.schema';
import { assetDetailsMigrationStrategies } from './assetDetails/assetDetails.migration';
import { assetsByAreaHeirachySchema } from './assetsByAreaHeirachy/assetsByAreaHierarchy.schema';
import { assetSectionDefinationsListMigrationStrategies } from './assetSectionDefination/assetSectionDefinition.migration';
import { assetSectionDefinationsListSchema } from './assetSectionDefination/assetSectionDefinition.schema';
import { componentChecklistSchema } from './componentChecklist/componentChecklist.schema';
import { componentRemovalCategoriesSchema } from './componentRemovalCategories/componentRemovalCategories.schema';
import { componentTypeSchema } from './componentType/componentType.schema';
import { conditionRatingSchema } from './conditionRating/conditionRating.schema';
import { deficiencyActionedListSchema } from './deficiencyActionedList/deficiencyActionedList.schema';
import { deficiencyCommentsSchema } from './deficiencyComments/deficiencyComments.schema';
import { deficiencyCommentsMigrationStrategies } from './deficiencyComments/deficiencyComments.migration';
import { deficiencyComponentRemovalSchema } from './deficiencyComponentRemoval/deficiencyComponentRemoval.schema';
import { deficiencyFilesSchema } from './deficiencyFiles/deficiencyFiles.schema';
import { deficiencyFilesMigrationStrategies } from './deficiencyFiles/deficiencyFiles.migration';
import { deficiencyListSchema } from './deficiencyList/deficiencyList.schema';
import { deficiencyListMigrationStrategies } from './deficiencyList/deficiencyList.migration';
import { deficiencyVersionFilesSchema } from './deficiencyVersionFiles/deficiencyVersionFiles.schema';
import { elevationRangeSchema } from './elevationRange/elevationRange.schema';
import { elevationRangeMigrationStrategies } from './elevationRange/elevationRange.migration';
import { environmentalConditionSchema } from './environmentalCondition/environmentalCondition.schema';
import { plannedDeficiencyListSchema } from './existingDeficiencyList/existingDeficiencyList.schema';
import { inspectionFilesSchema } from './inspectionFiles/inspectionFiles.schema';
import { inspectionListSchema } from './inspectionList/inspectionList.schema';
import { inspectionsListMigrationStrategies } from './inspectionList/inspectionList.migration';
import { lineandSectionPropertiesListSchema } from './lineandSectionPropertiesDetails/lineandSectionPropertiesDetails.schema';
import { lineandSectionPropertiesListMigrationStrategies } from './lineandSectionPropertiesDetails/lineandSectionPropertiesDetails.migration';
import { notificationSchema } from './notification/notification.schema';
import { notificationMigrationStrategies } from './notification/notification.migration';
import { operationalDowntimeSchema } from './operationalDowntime/operationalDowntime.schema';
import { plannedDeficiencyFilesSchema } from './plannedDeficiencyFiles/plannedDeficiencyFiles.schema';
import { pmCheckListSchema } from './pmCheckList/pmCheckList.schema';
import { pmCheckListMigrationStrategies } from './pmCheckList/pmCheckList.migration';
import { previousReportFilesSchema } from './previousReports/previousReports.schema';
import { previousReportFilesMigrationStrategies } from './previousReports/previousReports.migration';
import { previousTaskCommentsSchema } from './previousTaskComments/previousTaskComments.schema';
import { previousTaskCommentsMigrationStrategies } from './previousTaskComments/previousTaskComments.migration';
import { primaryCauseOfFailureSchema } from './primaryCauseOfFailure/primaryCauseOfFailure.schema';
import { remedialActionSchema } from './remedialAction/remedialAction.schema';
import { repairFactorSchema } from './repairFactor/repairFactor.schema';
import { riskAssessmentSchema } from './riskAssessment/riskAssessment.schema';
import { riskAssessmentMigrationStrategies } from './riskAssessment/riskAssessment.migration';
import { savePmChecklistSchema } from './savepmChecklist/savepmChecklist.schema';
import { signAndStampFileSchema } from './signAndStamp/signAndStamp.schema';
import { signAndStampFileMigrationStrategies } from './signAndStamp/signAndStamp.migration';
import { signAndStampUserConsentSchema } from './signAndStampUserConsent/signAndStampUserConsent.schema';
import { signAndStampUserLevelSchema } from './signAndStampUserLevel/signAndStampUserLevel.schema';
import { signAndStampUserLevelMigrationStrategies } from './signAndStampUserLevel/signAndStampUserLevel.migration';
import { taskListSchema } from './taskList/taskList.schema';
import { taskListSchemaMigrationStrategies } from './taskList/taskList.migration';
import { trafficSafetyScoreSchema } from './trafficSafetyScore/trafficSafetyScore.schema';
import { urgencySchema } from './urgency/urgency.schema';
import { viewInspectorSchema } from './viewInspector/viewInspector.schema';
import { viewInspectorMigrationStrategies } from './viewInspector/viewInspector.migration';
import { workPackageFilesSchema } from './workPackageFiles/workPackageFiles.schema';
import { workPackagesSchema } from './workPackages/workPackages.schema';
import { workPackagesMigrationStrategies } from './workPackages/workPackages.migration';

addRxPlugin(RxDBValidatePlugin);
addRxPlugin(RxDBDevModePlugin);
addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBMigrationPlugin);
addRxPlugin(RxDBUpdatePlugin);
addRxPlugin(RxDBReplicationGraphQLPlugin);
addRxPlugin(RxDBCleanupPlugin);
addPouchPlugin(memoryAdapter);
const currentTimestamp = dayjs().unix();
const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';

const initializeDB = async () => {
  // create RxDB instance

  let db;
  try {
    // Create RxDB Instance
    if (DEBUG_RXDB) console.log('Creating Rxdb 12 db...');
    db = await createRxDatabase<AimsDatabaseCollections>({
      name: 'aims_inspection_rxdb',
      storage: isJestTest()
        ? getRxStoragePouch('memory')
        : getRxStorageWorker({
            /**
             * The static methods of the RxStorage that is also
             * used inside of the worker process.
             */
            statics: RxStorageLokiStatics,
            /**
             * Contains any value that can be used as parameter
             * to the Worker constructor of thread.js
             * Most likely you want to put the path to the worker.js file in here.
             *
             * @link https://github.com/andywer/threads.js/
             */
            workerInput: '/lokijs-incremental-indexeddb.worker.js',
          }),

      ignoreDuplicate: true,
      cleanupPolicy: {
        minimumDeletedTime: 1000 * 60 * 60 * 24 * 14, // 14 Days,
      },
    });
  } catch (error) {
    console.log('🚀 ~ file: initializeDB.ts ~ line 138 ~ initializeDB ~ error', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    Sentry.captureException(error, {
      tags: {
        initializationError: true,
        rxDBError: true,
        currentTimestamp,
      },
    });

    // Always refresh in case of database intialization error
    if (!DEBUG_RXDB) window.location.reload();
  }

  // setup rxdb collections
  try {
    await db.addCollections({
      work_packages: {
        schema: workPackagesSchema,
        migrationStrategies: workPackagesMigrationStrategies,
      },
      view_inspector: {
        schema: viewInspectorSchema,
        migrationStrategies: viewInspectorMigrationStrategies,
      },
      pm_checklist: {
        schema: pmCheckListSchema,
        migrationStrategies: pmCheckListMigrationStrategies,
      },
      save_pmchecklist: {
        schema: savePmChecklistSchema,
      },
      inspection_list: {
        schema: inspectionListSchema,
        migrationStrategies: inspectionsListMigrationStrategies,
      },
      task_list: {
        schema: taskListSchema,
        migrationStrategies: taskListSchemaMigrationStrategies,
      },
      work_package_files: {
        schema: workPackageFilesSchema,
      },
      asset_details: {
        schema: assetDetailsSchema,
        migrationStrategies: assetDetailsMigrationStrategies,
      },
      inspection_files: {
        schema: inspectionFilesSchema,
      },
      sign_and_stamp_file: {
        schema: signAndStampFileSchema,
        migrationStrategies: signAndStampFileMigrationStrategies,
      },
      previous_reports_files: {
        schema: previousReportFilesSchema,
        migrationStrategies: previousReportFilesMigrationStrategies,
      },
      deficiency_list: {
        schema: deficiencyListSchema,
        migrationStrategies: deficiencyListMigrationStrategies,
      },
      deficiency_actioned_list: {
        schema: deficiencyActionedListSchema,
      },
      component_removal_categories: {
        schema: componentRemovalCategoriesSchema,
      },
      deficiency_component_removal: {
        schema: deficiencyComponentRemovalSchema,
      },
      risk_assessment: {
        schema: riskAssessmentSchema,
        migrationStrategies: riskAssessmentMigrationStrategies,
      },
      deficiency_files: {
        schema: deficiencyFilesSchema,
        migrationStrategies: deficiencyFilesMigrationStrategies,
      },
      primary_cause_of_failure: {
        schema: primaryCauseOfFailureSchema,
      },
      remedial_action: {
        schema: remedialActionSchema,
      },
      condition_rating: {
        schema: conditionRatingSchema,
      },
      operational_downtime: {
        schema: operationalDowntimeSchema,
      },
      traffic_safety_score: {
        schema: trafficSafetyScoreSchema,
      },
      environmental_condition: {
        schema: environmentalConditionSchema,
      },
      repair_factor: {
        schema: repairFactorSchema,
      },
      urgency: {
        schema: urgencySchema,
      },
      component_type: {
        schema: componentTypeSchema,
      },
      component_checklist: {
        schema: componentChecklistSchema,
      },
      assets_by_area_heirachy: {
        schema: assetsByAreaHeirachySchema,
      },
      notification: {
        schema: notificationSchema,
        migrationStrategies: notificationMigrationStrategies,
      },
      previous_task_comments: {
        schema: previousTaskCommentsSchema,
        migrationStrategies: previousTaskCommentsMigrationStrategies,
      },
      deficiency_comments: {
        schema: deficiencyCommentsSchema,
        migrationStrategies: deficiencyCommentsMigrationStrategies,
      },
      sign_and_stamp_user_consent: {
        schema: signAndStampUserConsentSchema,
      },
      sign_and_stamp_user_level: {
        schema: signAndStampUserLevelSchema,
        migrationStrategies: signAndStampUserLevelMigrationStrategies,
      },
      elevation_range: {
        schema: elevationRangeSchema,
        migrationStrategies: elevationRangeMigrationStrategies,
      },
      planned_deficiency_list: {
        schema: plannedDeficiencyListSchema,
      },
      planned_deficiency_files: {
        schema: plannedDeficiencyFilesSchema,
      },
      line_and_section_properties_list: {
        schema: lineandSectionPropertiesListSchema,
        migrationStrategies: lineandSectionPropertiesListMigrationStrategies,
      },
      asset_section_definations_list: {
        schema: assetSectionDefinationsListSchema,
        migrationStrategies: assetSectionDefinationsListMigrationStrategies,
      },
      deficiency_component_removal_version: {
        schema: deficiencyComponentRemovalSchema,
      },
      deficiency_version_comments: {
        schema: deficiencyCommentsSchema,
        migrationStrategies: deficiencyCommentsMigrationStrategies,
      },
      deficiency_version_files: {
        schema: deficiencyVersionFilesSchema,
      },
    });
  } catch (error) {
    console.log('🚀 ~ file: initializeDB.ts ~ line 298 ~ initializeDB ~ error', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    Sentry.captureException(error, {
      tags: {
        initializationError: true,
        rxDBError: true,
        currentTimestamp,
      },
    });
    // Always refresh in case of database intialization error
    if (!DEBUG_RXDB) window.location.reload();
  }
  return { db };
};

export default initializeDB;
